// One-tap share button that generates a shareable finish card and either:
//   1. Opens the native share sheet on mobile (Instagram, X, Messages, etc)
//   2. Falls back to download + copy caption to clipboard on desktop
//
// Usage:
//   <ShareResultButton
//     stats={{
//       name: profile.name,
//       distanceKm: 5.2, durationSec: 1938, paceSecPerKm: 372,
//       eventName: 'City Lights 10K',  // omit for personal runs
//       verified: true,
//       dateISO: new Date().toISOString(),
//       path: gpsPath,
//     }}
//   />

import { useState } from 'react';
import { Share2, Loader2, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { generateShareCard } from '../../lib/share-card';

function defaultCaption({ eventName, distanceKm, durationSec, verified }) {
  const km = distanceKm.toFixed(2);
  const t = formatTime(durationSec);
  if (eventName) {
    return `Just finished ${eventName} — ${km} km in ${t}${verified ? ' (verified ✓)' : ''}. Run, verified. rivl.run`;
  }
  return `Just ran ${km} km in ${t}. Run, verified. rivl.run`;
}

function formatTime(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(ss)}` : `${m}:${pad(ss)}`;
}

export function ShareResultButton({
  stats,
  className = '',
  variant = 'default',
  size = 'default',
  testid = 'share-result-btn',
  label = 'Share',
}) {
  const [busy, setBusy] = useState(false);

  async function handleShare() {
    setBusy(true);
    try {
      const blob = await generateShareCard(stats);
      if (!blob) throw new Error('Could not generate image');

      const caption = defaultCaption(stats);
      const file = new File([blob], 'rivl-finish.png', { type: 'image/png' });

      // Try native share (mobile) first — this opens Instagram/X/etc directly.
      const shareData = {
        title: stats.eventName || 'My RIVL Run',
        text: caption,
        files: [file],
      };

      if (navigator.canShare && navigator.canShare(shareData) && navigator.share) {
        await navigator.share(shareData);
        setBusy(false);
        return;
      }

      // Fallback: download + copy caption. Works on all desktop browsers.
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'rivl-finish.png';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      try {
        await navigator.clipboard.writeText(caption);
        toast.success('Card downloaded + caption copied. Paste into Instagram or X.', {
          duration: 6000,
        });
      } catch {
        toast.success('Card downloaded. Share it on Instagram or X.', { duration: 5000 });
      }
    } catch (err) {
      if (err?.name === 'AbortError') {
        // user cancelled the share sheet — silent
      } else {
        toast.error(err?.message || 'Could not share');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Button
      onClick={handleShare}
      disabled={busy}
      variant={variant}
      size={size}
      className={className}
      data-testid={testid}
    >
      {busy ? (
        <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
      ) : navigator.canShare ? (
        <Share2 className="h-4 w-4 mr-1.5" />
      ) : (
        <Download className="h-4 w-4 mr-1.5" />
      )}
      {label}
    </Button>
  );
}
